import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Editor } from '@tinymce/tinymce-react';
import { EditorBody, EditorContainer, EditorHeader } from './style';
import 'tinymce/tinymce';

// Stable across renders — a new init object would re-init TinyMCE and jump the caret.
const TINYMCE_INIT = {
  menubar: false,
  plugins: [
    'autolink lists link image charmap print preview anchor',
    'searchreplace visualblocks code fullscreen',
    'insertdatetime media table paste autoresize'
  ],
  valid_elements: 'li,br,p,ul',
  valid_children: '-li[ul]',
  content_style: `
      body {
        font-family: Roboto, Helvetica Neue, Helvetica, sans-serif;
        vertical-align: left;
        color: #444444;
      }
    `,
  toolbar: 'bullist outdent indent',
  branding: false,
  elementpath: false,
  statusbar: false
};

function RichText({
  className,
  headerComponent,
  value = '',
  onChange,
  lang = '',
  beforeChange,
  label: _label,
  handleLanguageChange: _handleLanguageChange,
  updateState: _updateState,
  ...rest
}) {
  const editorRef = useRef(null);
  const langRef = useRef(lang);
  const readyRef = useRef(false);
  const userEditedRef = useRef(false);
  const suppressRef = useRef(false);
  const toHtml = v => (typeof v === 'string' ? v : '');
  const appliedPropRef = useRef(toHtml(value));
  // Mount-only — tinymce-react resets caret if initialValue changes after init.
  const mountSeedRef = useRef(toHtml(value));

  const setHtml = html => {
    const next = toHtml(html);
    appliedPropRef.current = next;
    const editor = editorRef.current;
    if (!editor || !readyRef.current) return;
    if (next === editor.getContent()) {
      editor.nodeChanged();
      return;
    }

    // Avoid treating programmatic setContent as a user edit.
    suppressRef.current = true;
    try {
      editor.undoManager.ignore(() => {
        editor.setContent(next);
      });
      editor.undoManager.clear();
      editor.setDirty(false);
      editor.nodeChanged();
    } finally {
      suppressRef.current = false;
    }
  };

  useEffect(() => {
    const html = toHtml(value);

    if (langRef.current !== lang) {
      langRef.current = lang;
      userEditedRef.current = false;
      setHtml(html);
      return;
    }

    // Initial load / form reset only — never push frozen form value while typing.
    if (!userEditedRef.current && html !== appliedPropRef.current) {
      setHtml(html);
    }
  }, [lang, value]);

  const handleEditorChange = () => {
    if (suppressRef.current || !readyRef.current || !editorRef.current) return;
    userEditedRef.current = true;
    if (!onChange) return;
    const content = editorRef.current.getContent();
    onChange(beforeChange ? beforeChange(content, toHtml(value)) : content, 'tinymce');
  };

  return (
    <EditorContainer className={className} {...rest}>
      <EditorHeader data-test-id="rich-editor-header">{headerComponent}</EditorHeader>
      <EditorBody data-js-id="rich-editor-body">
        <Editor
          onInit={(_evt, editor) => {
            editorRef.current = editor;
            readyRef.current = true;
            // Value may have arrived while TinyMCE was still booting.
            setHtml(appliedPropRef.current || toHtml(value));
          }}
          initialValue={mountSeedRef.current}
          onEditorChange={handleEditorChange}
          init={TINYMCE_INIT}
        />
      </EditorBody>
    </EditorContainer>
  );
}

RichText.propTypes = {
  className: PropTypes.string,
  headerComponent: PropTypes.oneOfType([PropTypes.func, PropTypes.node, PropTypes.object]),
  label: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  lang: PropTypes.string,
  handleLanguageChange: PropTypes.func,
  beforeChange: PropTypes.func,
  updateState: PropTypes.bool
};

export default React.memo(RichText);
